interface TieredDiscountTier {
  id: string
  type: string
  subtitle: string
  quantity: number
  discountAmount: number
  title: string
  price: string
}

interface TieredDiscount {
  id: string
  mode: string
  collections: Array<{ id: string }>
  products: Array<{ id: string }>
  tiers: {
    edges: Array<{
      node: TieredDiscountTier
    }>
  }
}

interface CachedData {
  discounts: TieredDiscount[]
  timestamp: number
  hasNextPage: boolean
  cursor: string | null
}

interface GadgetFindManyOptions {
  first: number
  after?: string | null
  select: {
    mode: boolean
    collections: boolean
    products: boolean
    tiers: {
      edges: {
        node: {
          id: boolean
          type: boolean
          subtitle: boolean
          quantity: boolean
          discountAmount: boolean
          title: boolean
        }
      }
    }
  }
}

interface GadgetResponse {
  mode: string
  collections: Array<{ id: string }>
  products: Array<{ id: string }>
  tiers: {
    edges: Array<{
      node: TieredDiscountTier
    }>
  }
  pageInfo?: {
    hasNextPage: boolean
    endCursor: string
  }
}

interface GadgetInstance {
  tieredDiscounts: {
    findMany: (options: GadgetFindManyOptions) => Promise<GadgetResponse[]>
  }
}

export const tieredDiscountsStore = () => ({
  // State
  discounts: [] as TieredDiscount[],
  isLoading: false,
  isInitialized: false,
  error: null as string | null,

  // Cache settings
  cacheKey: 'tiered_discounts_cache',
  cacheExpiry: 10 * 60 * 1000, // 10 minutes in milliseconds
  pageSize: 250,

  // Gadget instance
  gadgetInstance: null as GadgetInstance | null,

  // Initialize the store
  async init () {
    if (this.isInitialized) return

    try {
      // Load cached data first (synchronously)
      const cached = this.getCachedData()
      if (cached && !cached.hasNextPage) {
        this.discounts = cached.discounts
        this.isInitialized = true
        console.log('Loaded discounts from cache:', this.discounts.length)

        // Dispatch custom event when store is ready
        document.dispatchEvent(new CustomEvent('tieredDiscountsStore:ready', {
          detail: { store: this }
        }))
        return
      }

      await this.initializeGadget()
      await this.loadDiscounts()
      this.isInitialized = true

      // Dispatch custom event when store is ready
      document.dispatchEvent(new CustomEvent('tieredDiscountsStore:ready', {
        detail: { store: this }
      }))
    } catch (error) {
      console.error('Failed to initialize tiered discounts store:', error)
      this.error = 'Failed to initialize'
    }
  },

  // Initialize Gadget client
  async initializeGadget () {
    if (this.gadgetInstance) return

    const env = Shopify.shop.includes('dev') ? '--development' : ''
    const script = document.createElement('script')
    script.src = `https://aussie-magnets-tiered-discount-v2${env}.gadget.app/api/client/web.min.js`
    script.defer = true

    return new Promise<void>((resolve, reject) => {
      script.onload = async () => {
        try {
          this.gadgetInstance = new Gadget()
          resolve()
        } catch (error) {
          reject(error)
        }
      }
      script.onerror = () => reject(new Error('Failed to load Gadget script'))
      document.head.appendChild(script)
    })
  },

  // Get cached data from localStorage
  getCachedData (): CachedData | null {
    try {
      const cached = localStorage.getItem(this.cacheKey)
      if (!cached) return null

      const data: CachedData = JSON.parse(cached)
      const now = Date.now()

      if (now - data.timestamp > this.cacheExpiry) {
        localStorage.removeItem(this.cacheKey)
        return null
      }

      return data
    } catch {
      return null
    }
  },

  // Set cached data to localStorage
  setCachedData (data: CachedData) {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify(data))
    } catch (error) {
      console.warn('Failed to cache tiered discounts data:', error)
    }
  },

  // Load all discounts (with pagination)
  async loadDiscounts () {
    this.isLoading = true
    this.error = null

    try {
      // Initialize if needed
      if (!this.gadgetInstance) {
        await this.initializeGadget()
      }

      let allDiscounts: TieredDiscount[] = []
      let hasNextPage = true
      let cursor: string | null = null
      let pageCount = 0

      // Dispatch initial loading event
      document.dispatchEvent(new CustomEvent('tieredDiscountsStore:loading', {
        detail: {
          page: 0,
          totalPages: 0,
          progress: 0
        }
      }))

      // Fetch all pages
      while (hasNextPage) {
        pageCount++

        // Dispatch progress event
        document.dispatchEvent(new CustomEvent('tieredDiscountsStore:loading', {
          detail: {
            page: pageCount,
            totalPages: pageCount + (hasNextPage ? 1 : 0),
            progress: Math.min((pageCount / (pageCount + 1)) * 100, 100)
          }
        }))

        const response = await this.gadgetInstance.tieredDiscounts.findMany({
          first: 250,
          after: cursor,
          select: {
            mode: true,
            collections: true,
            products: true,
            tiers: {
              edges: {
                node: {
                  id: true,
                  type: true,
                  subtitle: true,
                  quantity: true,
                  discountAmount: true,
                  title: true
                }
              }
            }
          }
        })

        // Log essential response info
        console.log('API Response received, length:', Array.isArray(response) ? response.length : 'Not an array')

        // Check if response has a different structure
        let discountsToProcess: unknown[] = []

        if (Array.isArray(response)) {
          discountsToProcess = response
        } else if (response && typeof response === 'object') {
          // Check if response is wrapped in a data property
          if (response.data && Array.isArray(response.data)) {
            discountsToProcess = response.data
          } else if (response.edges && Array.isArray(response.edges)) {
            // Check if it's using GraphQL edges structure
            discountsToProcess = response.edges.map((edge: { node: unknown }) => edge.node)
          } else {
            // Try to find any array property that might contain discounts
            const arrayProps = Object.keys(response).filter(key => Array.isArray(response[key]))
            if (arrayProps.length > 0) {
              discountsToProcess = response[arrayProps[0]]
            }
          }
        }

        // Filter active discounts and add to collection
        const activeDiscounts = discountsToProcess.filter(discount => {
          const discountObj = discount as { mode?: string }
          return discountObj && discountObj.mode === 'ACTIVE'
        }) as TieredDiscount[]

        console.log('Active discounts found:', activeDiscounts.length)

        allDiscounts = [...allDiscounts, ...activeDiscounts]

        // Dispatch progressive update event with new discounts
        document.dispatchEvent(new CustomEvent('tieredDiscountsStore:progressiveUpdate', {
          detail: {
            newDiscounts: activeDiscounts,
            totalDiscounts: allDiscounts,
            page: pageCount,
            hasMorePages: hasNextPage
          }
        }))

        // Check if there are more pages
        let pageInfo = null
        if (response && typeof response === 'object') {
          pageInfo = response.pagination?.pageInfo || response.pageInfo || response.page_info || response.page
        }

        if (pageInfo && pageInfo.hasNextPage) {
          hasNextPage = true
          cursor = pageInfo.endCursor || pageInfo.end_cursor
          console.log('Next page available, cursor:', cursor)
        } else {
          hasNextPage = false
          cursor = null
          console.log('No more pages available')
        }
      }

      console.log('Total discounts loaded:', allDiscounts.length)

      // Dispatch final loading event
      document.dispatchEvent(new CustomEvent('tieredDiscountsStore:loading', {
        detail: {
          page: pageCount,
          totalPages: pageCount,
          progress: 100
        }
      }))

      // Update store state
      this.discounts = allDiscounts

      // Cache results
      this.setCachedData({
        discounts: allDiscounts,
        timestamp: Date.now(),
        hasNextPage: false, // All pages fetched
        cursor: null
      })

      console.log('Cached data set. Store discounts length:', this.discounts.length)
    } catch (error) {
      console.error('Error loading tiered discounts:', error)
      this.error = 'Failed to load discounts'
    } finally {
      this.isLoading = false
    }
  },

  // Get discounts for a specific product
  getProductDiscounts (productId: string, productCollections: Array<{ id: number }>): TieredDiscountTier[] {
    if (!this.discounts.length) return []

    // Find applicable discount for this product
    const applicableDiscount = this.discounts.find(discount => {
      // Check collections
      if (discount.collections.length > 0) {
        const collectionIDs = discount.collections.map(collection =>
          Number(collection.id.split('/').pop())
        )
        return productCollections.some(collection =>
          collectionIDs.includes(collection.id)
        )
      }

      // Check specific products
      if (discount.products.length > 0) {
        const productIds = discount.products.map(product =>
          product.id.split('/').pop()
        )
        return productIds.includes(productId)
      }

      return false
    })

    if (!applicableDiscount) return []

    // Return the tiers (pricing will be calculated by the component)
    return applicableDiscount.tiers.edges.map(tier => ({
      ...tier.node,
      price: '' // Will be calculated by component
    }))
  },

  // Check if cache is expired
  isCacheExpired (): boolean {
    const cached = this.getCachedData()
    return !cached
  },

  // Manually clear cache
  clearCache () {
    localStorage.removeItem(this.cacheKey)
    this.discounts = []
    this.isInitialized = false
  },

  // Force refresh (clear cache and reload)
  async refresh () {
    this.clearCache()
    await this.loadDiscounts()
  }
})

export type { TieredDiscountTier, TieredDiscount }
